import * as Yup from 'yup'
import {
  buildGwsLicenseTaskInfo,
  getGwsDirectoryUserId,
  getGwsLicenseSkuIds,
  getGwsLicenseUserId,
  getGwsNotificationEmail,
} from '../../../util/employeeLicenses.util'
import { actions as GWS_ACTIONS } from './integrations/gws/actions'
import { actions as M365_ACTIONS } from './integrations/m365/actions'

// Modal Configuration
export const modalConfiguration = {
  // Render the 'Find an Employee' modal for the action
  renderFindAnEmployeeModal: {
    // Google Workspace
    [GWS_ACTIONS.GWS_PWD_RESET]: true,
    [GWS_ACTIONS.GWS_ADD_USER]: true,
    [GWS_ACTIONS.GWS_DELETE_USER]: true,
  },

  // If a task name is used by multiple action types, this will be the mapping to the correct action prior to firing off the task
  actionMapping: {
    // Microsoft 365
    [M365_ACTIONS.M365_ENABLE_USER]: M365_ACTIONS.M365_DISABLE_USER,

    // Google Workspace
    [GWS_ACTIONS.GWS_ENABLE_USER]: GWS_ACTIONS.GWS_DISABLE_USER,
  },
}

// Data loader configuration
export const dataLoaderConfiguration = {
  // Whether the action requires initial values for the formik wrapper
  actionRequiresInitialValues: {
    // Google Workspace
    [GWS_ACTIONS.GWS_PWD_RESET]: true,
    [GWS_ACTIONS.GWS_ADD_USER]: true,
  },

  // Whether the actions task parameters need to use a callback to be set or not
  actionTaskParametersRequireCallback: {
    // Microsoft 365
    [M365_ACTIONS.M365_MANAGE_GROUPS]: true,

    // Google Workspace
    [GWS_ACTIONS.GWS_PWD_RESET]: true,
    [GWS_ACTIONS.GWS_ADD_USER]: true,
    [GWS_ACTIONS.GWS_MANAGE_GROUPS]: true,
    [GWS_ACTIONS.GWS_MANAGE_LICENSE]: true,
    [GWS_ACTIONS.GWS_DELETE_USER]: true,
  },

  actionSendsMultipleTasks: {
    // Google Workspace
    [GWS_ACTIONS.GWS_DELETE_USER]: true,
  },
}

// Retrieves the integration name for the action
// eslint-disable-next-line consistent-return
export const getIntegrationName = (action, intl) => {
  switch (action) {
    case M365_ACTIONS.M365_DISABLE_USER:
    case M365_ACTIONS.M365_ENABLE_USER_MFA:
    case M365_ACTIONS.M365_MANAGE_GROUPS:
      return intl.formatMessage({ id: 'ENGAGE.MICROSOFT_365' })
    case M365_ACTIONS.M365_MANAGE_SECURITY_GROUPS:
    case M365_ACTIONS.M365_MANAGE_SHARED_GROUPS:
    case M365_ACTIONS.M365_MANAGE_DISTRIBUTION_GROUPS:
      return intl.formatMessage({ id: 'ENGAGE.MICROSOFT_365' })
    case GWS_ACTIONS.GWS_PWD_RESET:
    case GWS_ACTIONS.GWS_DISABLE_USER:
    case GWS_ACTIONS.GWS_ENABLE_USER:
    case GWS_ACTIONS.GWS_ADD_USER:
    case GWS_ACTIONS.GWS_REVOKE_SESSIONS:
    case GWS_ACTIONS.GWS_MANAGE_GROUPS:
    case GWS_ACTIONS.GWS_DISABLE_USER_MFA:
    case GWS_ACTIONS.GWS_MANAGE_LICENSE:
    case GWS_ACTIONS.GWS_DELETE_USER:
      return intl.formatMessage({ id: 'ENGAGE.GOOGLE_WORKSPACE' })
    default:
      break
  }
}

// Retrieves the main modal header title. Note: If a title change is required for a certain render state, manage the change from within the action-component itself
// eslint-disable-next-line consistent-return
export const getHeaderTitleId = (action) => {
  switch (action) {
    case GWS_ACTIONS.GWS_PWD_RESET:
      return 'ENGAGE.RESET_PASSWORD_TITLE'
    case M365_ACTIONS.M365_DISABLE_USER:
      return 'ENGAGE.BLOCK_SIGN_IN_TITLE'
    case M365_ACTIONS.M365_ENABLE_USER_MFA:
      return 'ENGAGE.ENABLE_MFA'
    case GWS_ACTIONS.GWS_ADD_USER:
      return 'ENGAGE.ADD_USER_TITLE'
    case GWS_ACTIONS.GWS_DISABLE_USER:
      return 'ENGAGE.SUSPEND_SIGN_IN_TITLE'
    case GWS_ACTIONS.GWS_ENABLE_USER:
      return 'ENGAGE.UNSUSPEND_SIGN_IN_TITLE'
    case GWS_ACTIONS.GWS_REVOKE_SESSIONS:
      return 'ENGAGE.SIGN_OUT_ALL_APPS_TITLE'
    case M365_ACTIONS.M365_MANAGE_GROUPS:
      return 'ENGAGE.MANAGE_M365_GROUPS'
    case GWS_ACTIONS.GWS_MANAGE_GROUPS:
      return 'ENGAGE.MANAGE_GROUPS_TITLE'
    case GWS_ACTIONS.GWS_DISABLE_USER_MFA:
      return 'ENGAGE.DISABLE_MFA_TITLE'
    case GWS_ACTIONS.GWS_MANAGE_LICENSE:
      return 'ENGAGE.MANAGE_LICENSES_TITLE'
    case GWS_ACTIONS.GWS_DELETE_USER:
      return 'ENGAGE.OFFBOARD_USER_TITLE'
    case M365_ACTIONS.M365_MANAGE_SECURITY_GROUPS:
      return 'ENGAGE.MANAGE_SECURITY_GROUPS_TITLE'
    case M365_ACTIONS.M365_MANAGE_SHARED_GROUPS:
      return 'ENGAGE.MANAGE_SHARED_GROUPS_TITLE'
    case M365_ACTIONS.M365_MANAGE_DISTRIBUTION_GROUPS:
      return 'ENGAGE.MANAGE_DISTRIBUTION_GROUPS_TITLE'
    default:
      break
  }
}

// Retrieves the header styling
export const getHeaderStyle = (action) => {
  switch (action) {
    default:
      // For actions with multiple tabs, this styles the header for additional room
      return {
        backgroundColor: 'var(--aug-surface-page-alt)',
        border: 'none',
        borderBottom: '1px solid var(--aug-border-panel)',
      }
  }
}

// Retrieves the initial submit button text id. If it changes throughout the render state, set the submitButtonLangId and pass it to the footer
// eslint-disable-next-line consistent-return
export const getSubmitButtonText = (action) => {
  switch (action) {
    case GWS_ACTIONS.GWS_ADD_USER:
    case GWS_ACTIONS.GWS_MANAGE_LICENSE:
      return 'GENERAL.NEXT'
    case GWS_ACTIONS.GWS_DELETE_USER:
      return 'ENGAGE.DELETE_USER_APPLY_AND_GO_NEXT'
    case M365_ACTIONS.M365_DISABLE_USER:
      return 'ENGAGE.BLOCK_SIGNIN'
    case M365_ACTIONS.M365_ENABLE_USER_MFA:
      return 'ENGAGE.ENABLE_MFA'
    case GWS_ACTIONS.GWS_PWD_RESET:
      return 'ENGAGE.RESET_PASSWORD'
    case GWS_ACTIONS.GWS_DISABLE_USER:
      return 'ENGAGE.SUSPEND_SIGN_IN'
    case GWS_ACTIONS.GWS_ENABLE_USER:
      return 'ENGAGE.UNSUSPEND_SIGN_IN'
    case M365_ACTIONS.M365_MANAGE_SHARED_GROUPS:
      return 'ENGAGE.UPDATE_SHARED_MAILBOXES'
    case M365_ACTIONS.M365_MANAGE_SECURITY_GROUPS:
      return 'ENGAGE.UPDATE_SECURITY_MAILBOXES'
    case M365_ACTIONS.M365_MANAGE_DISTRIBUTION_GROUPS:
      return 'ENGAGE.UPDATE_DISTRIBUTION_GROUPS'
    case M365_ACTIONS.M365_MANAGE_GROUPS:
      return 'ENGAGE.UPDATE_M356_MAILBOXES'
    case GWS_ACTIONS.GWS_MANAGE_GROUPS:
      return 'ENGAGE.UPDATE_GROUPS'
    case GWS_ACTIONS.GWS_DISABLE_USER_MFA:
      return 'ENGAGE.DISABLE_MFA_TITLE'
    default:
      break
  }
}

/**
 * Retrieves any success data that is required for displaying a success body.
 * successActions specifies all possible actions an integration-action can do.
 * An action may not use all actionId's for each possible flow, so utilize the actionData from IM to specify which action to display
 *
 * actionId: This is the completed action id that is set during the render state of the component. It symbolizes which sub-actions were completed throughout the entire action
 * displayAction: This is used IFF an actionId is an array of a single actionId that was completed. For example, a user is added to multiple groups, display an action row for each successful group addition
 * displayActionObjectKey: This is used IFF displayAction is used. This is if displayAction is made up of objects, this is the key to the value of the object
 * displayBelow: Used when an element is intended to be displayed below the actionId row. It may be a single row item, or an iterable array.
 * displayBelowObjectKey: If displayBelow is an array of objects, this is the key of it's objects corresponding to which value to display
 * actionIdEnJsonVariableId: This is used IFF display below is displaying an en.json formatted message that requies a variable input
 *
 * @param {String} action The action for the integration
 */
// eslint-disable-next-line consistent-return
export const getSuccessData = (action) => {
  switch (action) {
    case M365_ACTIONS.M365_ENABLE_USER_MFA:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.MFA_ENABLED_SUCCESSFULLY',
          },
        ],
      }
    case GWS_ACTIONS.GWS_PWD_RESET:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.RESET_PASSWORD_SUCCESS',
          },
        ],
      }
    case M365_ACTIONS.M365_DISABLE_USER:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.BLOCK_SIGN_IN_SUCCESS',
          },
        ],
      }
    case GWS_ACTIONS.GWS_ADD_USER:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.EMPLOYEE_CREATED',
            displayBelow: 'newEmployeeName',
          },
          {
            actionId: 'ENGAGE.ACCOUNT_CREATED',
            displayBelow: 'newEmployeeEmail',
          },
          {
            actionId: 'ENGAGE.LICENSES_ASSIGNED',
            displayBelow: 'licensesAssigned',
            displayBelowObjectKey: 'displayName',
          },
          {
            actionId: 'ENGAGE.ADDED_TO_GROUP',
            displayAction: 'groups', // If an actionId is to be iterated over and displayed, this is the value of the iterable within the actionData
            displayActionObjectKey: 'name', // If the displayAction is an array of objects, this is the field within the object to display
            actionIdEnJsonVariableId: 'groupName', // This is the variable in en.json that is used to insert a value into a formatted message
          },
        ],
      }
    case GWS_ACTIONS.GWS_DISABLE_USER:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.SUSPEND_SIGN_IN_SUCCESS',
          },
        ],
      }
    case GWS_ACTIONS.GWS_ENABLE_USER:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.UNSUSPEND_SIGN_IN_SUCCESS',
          },
        ],
      }
    case GWS_ACTIONS.GWS_REVOKE_SESSIONS:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.SIGNED_OUT_ALL_APPS',
          },
        ],
      }
    case M365_ACTIONS.M365_MANAGE_SECURITY_GROUPS:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.ADDED_TO_SECURITY_GROUPS',
            displayAction: 'groupsAdded',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
          {
            actionId: 'ENGAGE.REMOVED_FROM_SECURITY_GROUPS',
            displayAction: 'groupsRemoved',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
        ],
      }
    case M365_ACTIONS.M365_MANAGE_SHARED_GROUPS:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.ADDED_TO_SHARED_GROUPS',
            displayAction: 'groupsAdded',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
          {
            actionId: 'ENGAGE.REMOVED_FROM_SHARED_MAILBOXES',
            displayAction: 'groupsRemoved',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
        ],
      }
    case M365_ACTIONS.M365_MANAGE_DISTRIBUTION_GROUPS:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.ADDED_TO_DISTRIBUTION_GROUPS',
            displayAction: 'groupsAdded',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
          {
            actionId: 'ENGAGE.REMOVED_FROM_DISTRIBUTION_GROUPS',
            displayAction: 'groupsRemoved',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
        ],
      }
    case M365_ACTIONS.M365_MANAGE_GROUPS:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.ADDED_TO_M365_GROUP',
            displayAction: 'groupsAdded',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
          {
            actionId: 'ENGAGE.REMOVED_FROM_365_GROUP',
            displayAction: 'groupsRemoved',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
        ],
      }
    case GWS_ACTIONS.GWS_MANAGE_GROUPS:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.ADDED_TO_GROUP',
            displayAction: 'groupsAdded',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
          {
            actionId: 'ENGAGE.REMOVED_FROM_GROUP',
            displayAction: 'groupsRemoved',
            displayActionObjectKey: 'name',
            actionIdEnJsonVariableId: 'groupName',
            displayBelow: 'employeesUpdated',
            displayBelowObjectKey: 'employeeFullName',
          },
        ],
      }
    case GWS_ACTIONS.GWS_DISABLE_USER_MFA:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.DISABLE_MFA_SUCCESS',
            displayBelow: 'employeesGettingMfaDisabled', // An array added to the actionData
            displayBelowObjectKey: 'employeeFullName', // The key in of the employee objects in the above array
          },
        ],
      }
    case GWS_ACTIONS.GWS_MANAGE_LICENSE:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.LICENSES_ASSIGNED',
            displayBelow: 'licensesAssigned',
            displayBelowObjectKey: 'displayName',
          },
          {
            actionId: 'ENGAGE.LICENSES_REMOVED',
            displayBelow: 'licensesRemoved',
            displayBelowObjectKey: 'displayName',
          },
        ],
      }
    case GWS_ACTIONS.GWS_DELETE_USER:
      return {
        successActions: [
          {
            actionId: 'ENGAGE.GWS_USER_REMOVED_SUCCESS',
          },
          {
            actionId: 'ENGAGE.RESET_PASSWORD_SUCCESS',
          },
          {
            actionId: 'ENGAGE.SUSPEND_SIGN_IN_SUCCESS',
          },
          {
            actionId: 'ENGAGE.SIGNED_OUT_ALL_APPS',
          },
          {
            actionId: 'ENGAGE.UNASSIGNED_SUCCESS',
            displayBelow: 'licensesUnassigned',
            displayBelowObjectKey: 'displayName',
          },
          {
            actionId: 'ENGAGE.DELETING_EMPLOYEE_SUCCESS',
          },
          {
            actionId: 'ENGAGE.TRANSFER_DATA_SUCCESS',
            displayBelow: 'transferToEmployee',
            displayBelowObjectKey: 'employeeEmail',
          },
        ],
      }
    default:
      break
  }
}

// Retrieves the loading data for an action
// eslint-disable-next-line consistent-return
export const getLoadingData = (action) => {
  switch (action) {
    case M365_ACTIONS.M365_ENABLE_USER_MFA:
      return {
        loadingPhraseId: 'ENGAGE.ENABLING_MFA',
      }
    case GWS_ACTIONS.GWS_PWD_RESET:
      return {
        loadingPhraseId: 'ENGAGE.RESETTING_PASSWORD',
      }
    case M365_ACTIONS.M365_DISABLE_USER:
      return {
        loadingPhraseId: 'ENGAGE.BLOCKING_SIGN_IN',
      }
    case GWS_ACTIONS.GWS_ADD_USER:
      return {
        loadingPhraseId: 'ENGAGE.ADDING_USER',
      }
    case GWS_ACTIONS.GWS_DISABLE_USER:
      return {
        loadingPhraseId: 'ENGAGE.SUSPENDING_SIGN_IN',
      }
    case GWS_ACTIONS.GWS_ENABLE_USER:
      return {
        loadingPhraseId: 'ENGAGE.UNSUSPENDING_SIGN_IN',
      }
    case GWS_ACTIONS.GWS_REVOKE_SESSIONS:
      return {
        loadingPhraseId: 'ENGAGE.SIGNING_OUT_ALL_APPS',
      }
    case M365_ACTIONS.M365_MANAGE_SHARED_GROUPS:
      return {
        loadingPhraseId: 'ENGAGE.UPDATING_SHARED_MAILBOXES',
      }
    case M365_ACTIONS.M365_MANAGE_SECURITY_GROUPS:
      return {
        loadingPhraseId: 'ENGAGE.UPDATING_SECURITY_GROUPS',
      }
    case M365_ACTIONS.M365_MANAGE_DISTRIBUTION_GROUPS:
      return {
        loadingPhraseId: 'ENGAGE.UPDATING_DISTRIBUTION_GROUPS',
      }
    case M365_ACTIONS.M365_MANAGE_GROUPS:
      return {
        loadingPhraseId: 'ENGAGE.UPDATING_M365_GROUPS',
      }
    case GWS_ACTIONS.GWS_MANAGE_GROUPS:
      return {
        loadingPhraseId: 'ENGAGE.UPDATING_GROUPS',
      }
    case GWS_ACTIONS.GWS_DISABLE_USER_MFA:
      return {
        loadingPhraseId: 'ENGAGE.DISABLING_MFA',
      }
    case GWS_ACTIONS.GWS_MANAGE_LICENSE:
      return {
        loadingPhraseId: 'ENGAGE.UPDATING_LICENSES',
      }
    case GWS_ACTIONS.GWS_DELETE_USER:
      return {
        loadingPhraseId: 'ENGAGE.SECURING_ACCOUNT',
      }
    default:
      break
  }
}

// Retrieve the error data for an action
export const getErrorData = () => ({
  // eslint-disable-next-line no-use-before-define
  errorMappings: getErrorMappings(),
})

// Retrieves the errorMappings for an action
export const getErrorMappings = () => ({
  MISSING_PARAMETER_EMPLOYEE_IDENTIFIER: 'MISSING_PARAMETER_EMPLOYEE_IDENTIFIER',
  ADD_LIC_FAILED: 'ADD_LIC_FAILED',
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  ACTION_COMPLETED_SEND_EMAIL_FAILED: 'ACTION_COMPLETED_SEND_EMAIL_FAILED',
  MISSING_PARAMETER_PASSWORD_INFO: 'MISSING_PARAMETER_PASSWORD_INFO',
  ACTION_COMPLETED_WITH_ERROR: 'ACTION_COMPLETED_WITH_ERROR',
})

/**
 * Retrieves the task parameters for the specified action.
 * This will either return an object with the final task parameters set (when the integration manager has all required information for the task)
 * It may also return a callback function with parameters that are set by the action-component. In this case, the modal footer will retrieve this
 * data from its props and use getFinalValuesFromTaskParameterFunction() to set the final task parameter object
 * @param {String} action The action/task
 * @param {Object} taskParameterData Used when the task parameter data does not require data to be set by the action-component
 */
// eslint-disable-next-line consistent-return
export const getTaskParameters = (action, taskParameterData) => {
  switch (action) {
    case M365_ACTIONS.M365_ENABLE_USER_MFA:
      return () => (employeeGuids, selectedCustomer, nonBrowserSignIn, mfaType) => {
        if (!employeeGuids) {
          throw new Error('Error - Missing a required task parameter: employeeGuids')
        } else if (!selectedCustomer) {
          throw new Error('Error - Missing a required task parameter: selectedCustomer')
        }
        return {
          employeeGuids,
          selectedCustomer: selectedCustomer?.id,
          nonBrowserSignIn: !!nonBrowserSignIn,
          mfaType: mfaType != null ? mfaType : 'none', // Expected value from probe when null is "none"
        }
      }
    case M365_ACTIONS.M365_DISABLE_USER:
      if (!taskParameterData.selectedEmployee) {
        throw new Error('Error - Missing a required task parameter: selectedEmployee')
      }
      return {
        employeeIdentifier: taskParameterData.selectedEmployee?.employeeIdentifier,
        enableUser: false,
      }

    case GWS_ACTIONS.GWS_ADD_USER:
      return () => (selectedEmployee, values, isResettingPassword, licenseIdsToAdd, groupIdsToAdd, phoneNumber) => ({
        employeeIdentifier: selectedEmployee?.employeeIdentifier ? selectedEmployee.employeeIdentifier : undefined, // Leave as undefined if it is a new employee
        firstName: selectedEmployee?.firstName ? selectedEmployee?.firstName : values.firstName,
        lastName: selectedEmployee?.lastName ? selectedEmployee?.lastName : values.lastName,
        displayName: selectedEmployee?.displayName ? selectedEmployee?.displayName : '',
        email:
          selectedEmployee?.employeeIdentifier && selectedEmployee?.email
            ? selectedEmployee?.email
            : `${values.username}@${values.domain.value}`,
        department: selectedEmployee?.department ? selectedEmployee?.department : values.department,
        jobTitle: selectedEmployee?.jobTitle ? selectedEmployee?.jobTitle : values.jobTitle,
        phoneNumber,
        passwordInfo: isResettingPassword
          ? {
              newPassword: values.newPassword,
              // Empty list when "Send password in email upon completion" is off
              sendPasswordTo:
                values.sendPasswordTo === false
                  ? []
                  : [values.employee ? values.employee?.value?.email : values.email].filter(Boolean),
            }
          : {},
        licenseInfo: {
          toBeAdded: licenseIdsToAdd,
          toBeRemoved: [],
        },
        groupsInfo: {
          toBeAdded: groupIdsToAdd,
          toBeRemoved: [],
        },
      })
    case GWS_ACTIONS.GWS_DISABLE_USER:
      if (!taskParameterData.selectedEmployee) {
        throw new Error('Error - Missing a required task parameter: selectedEmployee')
      }
      return {
        employeeEmail: getGwsDirectoryUserId(taskParameterData.selectedEmployee),
        employeeIdentifier: taskParameterData.selectedEmployee?.employeeIdentifier,
        suspended: true,
      }
    case GWS_ACTIONS.GWS_ENABLE_USER:
      if (!taskParameterData.selectedEmployee) {
        throw new Error('Error - Missing a required task parameter: selectedEmployee')
      }
      return {
        employeeEmail: getGwsDirectoryUserId(taskParameterData.selectedEmployee),
        employeeIdentifier: taskParameterData.selectedEmployee?.employeeIdentifier,
        suspended: false,
      }
    case GWS_ACTIONS.GWS_PWD_RESET:
      return () => (selectedEmployee, values) => {
        if (!selectedEmployee) {
          throw new Error('Error - Missing a required task parameter: selectedEmployee')
        }
        if (!values) {
          throw new Error('Error - Missing a required task parameter: values')
        }
        const recipientEmail = values?.employee?.value?.email || values?.email
        return {
          employeeIdentifier: null,
          employeeEmail: getGwsDirectoryUserId(selectedEmployee),
          employeeDisplayEmail: getGwsNotificationEmail(selectedEmployee),
          passwordInfo: {
            newPassword: values?.newPassword,
            sendPasswordTo: recipientEmail ? [recipientEmail] : [],
          },
        }
      }
    case GWS_ACTIONS.GWS_REVOKE_SESSIONS:
      if (!taskParameterData.selectedEmployee) {
        throw new Error('Error - Missing a required task parameter: selectedEmployee')
      }
      return {
        employeeEmail: getGwsDirectoryUserId(taskParameterData.selectedEmployee),
        employeeIdentifier: taskParameterData.selectedEmployee?.employeeIdentifier,
      }
    case M365_ACTIONS.M365_MANAGE_SHARED_GROUPS:
    case M365_ACTIONS.M365_MANAGE_DISTRIBUTION_GROUPS:
      return () => (employeeEmails, groupIdsToAdd, groupIdsToRemove) => {
        if (!employeeEmails?.length) {
          throw new Error('Error - Missing a required task parameter: employeeEmails')
        }
        return {
          employeeEmail: employeeEmails,
          groupsInfo: {
            toBeAdded: groupIdsToAdd,
            toBeRemoved: groupIdsToRemove,
          },
        }
      }
    case M365_ACTIONS.M365_MANAGE_GROUPS:
    case M365_ACTIONS.M365_MANAGE_SECURITY_GROUPS:
      return () => (employeeIdentifiers, groupIdsToAdd, groupIdsToRemove) => {
        if (!employeeIdentifiers?.length) {
          throw new Error('Error - Missing a required task parameter: employeeIdentifiers')
        }
        return {
          employeeIdentifiers,
          groupsInfo: {
            toBeAdded: groupIdsToAdd,
            toBeRemoved: groupIdsToRemove,
          },
        }
      }
    case GWS_ACTIONS.GWS_MANAGE_GROUPS:
      return () => (employeeEmails, groupIdsToAdd, groupIdsToRemove) => {
        if (!employeeEmails?.length) {
          throw new Error('Error - Missing a required task parameter: employeeEmails')
        }
        return {
          employeeEmail: employeeEmails,
          groupsInfo: {
            toBeAdded: groupIdsToAdd,
            toBeRemoved: groupIdsToRemove,
          },
        }
      }
    case GWS_ACTIONS.GWS_DISABLE_USER_MFA:
      return () => (employeesWithMfaEnabled) => {
        if (!employeesWithMfaEnabled?.length) {
          throw new Error('Error - Missing required task parameter: employeesWithMfaEnabled')
        }

        return {
          employeeGuids: employeesWithMfaEnabled.map((e) => e.employeeIdentifier),
        }
      }
    case GWS_ACTIONS.GWS_MANAGE_LICENSE:
      return () => (selectedEmployee, licenseIdsToAdd, licenseIdsToRemove) => ({
        employeeEmail: getGwsLicenseUserId(selectedEmployee),
        employeeIdentifier: getGwsDirectoryUserId(selectedEmployee),
        licenseInfo: buildGwsLicenseTaskInfo({
          toBeAdded: getGwsLicenseSkuIds(licenseIdsToAdd),
          toBeRemoved: getGwsLicenseSkuIds(licenseIdsToRemove),
          currentlyAssigned: getGwsLicenseSkuIds((selectedEmployee?.licenseType || []).map((license) => license.id)),
        }),
      })
    case GWS_ACTIONS.GWS_DELETE_USER:
      return () =>
        (
          selectedEmployee,
          revokeSessions,
          suspended,
          resetPassword,
          deleteGWSUser,
          transferData,
          transferToEmployeeIdentifier,
          transferDrivesAndDocs,
          includeNotSharedDrives,
          transferCalender,
          includeAllCalenderResources,
          transferBrandAccounts,
          transferDataStudio,
          includeNotSharedCalender,
        ) => ({
          employeeIdentifier: selectedEmployee?.employeeIdentifier,
          employeeEmail: getGwsDirectoryUserId(selectedEmployee),
          revokeSessions,
          suspended,
          resetPassword,
          delete: deleteGWSUser,
          dataTransfer: transferData,
          dataTransferOptions: {
            newEmployeeIdentifier: transferToEmployeeIdentifier,
            drivesAndDocs: {
              transfer: transferDrivesAndDocs,
              includeNotShared: includeNotSharedDrives,
            },
            calender: {
              transfer: transferCalender,
              includeAllCalenderResources,
            },
            brandAccounts: transferBrandAccounts,
            dataStudio: {
              transfer: transferDataStudio,
              includeNotShared: includeNotSharedCalender,
            },
          },
        })
    default:
      break
  }
}

/**
 * Retrieves the final task parameter object. This is only used when the action required a callback function for the taskParameters
 * @param {String} action The action/task
 * @param {Function} taskParameterFunction The task parameter callback function from config -> getTaskParameters()
 * @param {Object} parameters The parameters for all possible task parameter callback functions (Note: only the parameters used by the current task may be set)
 */
export const getFinalValuesFromTaskParameterFunction = (
  action,
  taskParameterFunction,
  parameters, // eslint-disable-next-line consistent-return
) => {
  switch (action) {
    case M365_ACTIONS.M365_ENABLE_USER_MFA:
      return taskParameterFunction(
        parameters.actionData.employeeGuids,
        parameters.actionData.selectedCustomer,
        parameters.actionData.nonBrowserSignIn,
        parameters.actionData.mfaType,
      )
    case GWS_ACTIONS.GWS_PWD_RESET:
      return taskParameterFunction(parameters.selectedEmployee, parameters.values)
    case GWS_ACTIONS.GWS_ADD_USER:
      return taskParameterFunction(
        parameters.selectedEmployee,
        parameters.actionData?.values,
        parameters.actionData?.isResettingPassword,
        parameters.actionData?.licenseIdsToAdd,
        parameters.actionData?.groupIdsToAdd,
        parameters.actionData?.phoneNumber,
      )
    case GWS_ACTIONS.GWS_REVOKE_SESSIONS:
      return taskParameterFunction(parameters.selectedEmployee)
    case M365_ACTIONS.M365_MANAGE_SHARED_GROUPS:
    case M365_ACTIONS.M365_MANAGE_DISTRIBUTION_GROUPS:
      return taskParameterFunction(
        parameters.actionData?.employeeEmails,
        parameters.actionData?.groupIdsToAdd,
        parameters.actionData?.groupIdsToRemove,
      )
    case M365_ACTIONS.M365_MANAGE_GROUPS:
    case M365_ACTIONS.M365_MANAGE_SECURITY_GROUPS:
      return taskParameterFunction(
        parameters.actionData?.employeeIdentifiers,
        parameters.actionData?.groupIdsToAdd,
        parameters.actionData?.groupIdsToRemove,
      )
    case GWS_ACTIONS.GWS_MANAGE_GROUPS:
      return taskParameterFunction(
        parameters.actionData?.employeeEmails,
        parameters.actionData?.groupIdsToAdd,
        parameters.actionData?.groupIdsToRemove,
      )
    case GWS_ACTIONS.GWS_DISABLE_USER_MFA:
      return taskParameterFunction(parameters.actionData?.employeesWithMfaEnabled)
    case GWS_ACTIONS.GWS_MANAGE_LICENSE:
      return taskParameterFunction(
        parameters.selectedEmployee,
        parameters.actionData?.licenseIdsToAdd,
        parameters.actionData?.licenseIdsToRemove,
      )
    case GWS_ACTIONS.GWS_DELETE_USER:
      return taskParameterFunction(
        parameters.selectedEmployee,
        parameters.actionData?.revokeSessions,
        parameters.actionData?.suspended,
        parameters.actionData?.resetPassword,
        parameters.actionData?.delete,
        parameters.actionData?.transferData,
        parameters.actionData?.transferToEmployeeIdentifier,
        parameters.actionData?.transferDrivesAndDocs,
        parameters.actionData?.includeNotSharedDrives,
        parameters.actionData?.transferCalender,
        parameters.actionData?.includeAllCalenderResources,
        parameters.actionData?.transferBrandAccounts,
        parameters.actionData?.transferDataStudio,
        parameters.actionData?.includeNotSharedCalender,
      )
    default:
      break
  }
}

// Retrieves the formik schema for the action
export const getFormikSchema = (action, intl, options, key = false) => {
  let schema
  const MIN_PASSWORD_LENGTH = 8
  const passwordRegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-“!@#%&\/,><\’:;|_~`])\S{8,99}$/ // eslint-disable-line no-useless-escape

  // TODO Change to correct password regex that Tanzeel created

  const fieldLengthRequirement = (minimumLength, errorId) =>
    Yup.string().min(minimumLength, intl.formatMessage({ id: errorId }))

  // List of form validations
  const fieldRequired = Yup.string().required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
  const passwordLength = fieldLengthRequirement(MIN_PASSWORD_LENGTH, 'AUTH.VALIDATION.TOO_SHORT')
  const passwordStrong = Yup.string().matches(
    passwordRegExp,
    intl.formatMessage({ id: 'AUTH.VALIDATION.INVALID_PASSWORD' }),
  )

  switch (action) {
    case GWS_ACTIONS.GWS_PWD_RESET:
      // Strong password requirement, password length requirement, required field requirement if the password is not being auto generated
      schema = Yup.object().shape({
        newPassword: passwordLength
          .concat(passwordStrong)
          .concat(!options.isAutoGeneratingPassword ? fieldRequired : null),
      })
      break
    case GWS_ACTIONS.GWS_ADD_USER:
      schema = Yup.object().shape({
        department: undefined,
        jobTitle: undefined,
        firstName: options.activeKey === 'add-employee' && !key ? fieldRequired : undefined,
        lastName: options.activeKey === 'add-employee' && !key ? fieldRequired : undefined,
        username: options.activeKey === 'account-details' && !key ? fieldRequired : undefined,
        newPassword:
          options.activeKey === 'account-details' && !options.isAutoGeneratingPassword
            ? passwordLength.concat(passwordStrong).concat(fieldRequired)
            : undefined,
      })
      break
    default:
      break
  }

  return schema
}

// Retrieves the intial values for the action
// eslint-disable-next-line consistent-return
export const getInitialValues = (action, currentUser, selectedEmployee) => {
  switch (action) {
    case GWS_ACTIONS.GWS_PWD_RESET:
      return {
        changePasswordAtNextLogin: true,
        sendPasswordTo: true,
        newPassword: '',
        email: currentUser?.amazon_id,
        employee: undefined,
      }
    case GWS_ACTIONS.GWS_ADD_USER:
      return {
        firstName: undefined,
        lastName: undefined,
        department: undefined,
        email: currentUser?.amazon_id,
        username: selectedEmployee?.email?.split('@')[0],
        domain: selectedEmployee?.email?.split('@')[1],
        jobTitle: undefined,
        country: undefined,
        sendPasswordTo: true,
        isResettingPassword: true,
        employeeDetails: undefined,
      }
    default:
      console.error('The action specified does not have any intitial values set!')
  }
}
